import { Command } from "@ckeditor/ckeditor5-core";

import randomUUIDGenerator from '../../src/utils/randomUUIDGenerator.js';

import { CkeditorConstants, checkSelectedComments, subtemplateUtils, checkSelectedLink } from '../../src/utils';

const {
    command: { CLICK },
    event: { KEYUP, SELECTION_CHANGE },
    PLUGINS: { RELEASE_NOTES },
    class: { REF },
    htmlAttributes: { CLASS, REL },
    helperAttributes: { LINK_HREF },
    releaseNotesRDFAAttributes,
    linksTo
} = CkeditorConstants
export class ReleaseNotesCommand extends Command {

    constructor(editor) {
        super(editor);
        this.refresh();
    }

    execute({ isUMCReleaseNote = false, threadId: rawThreadId, threadId2: rawThreadId2 } = {}) {
        const editor = this.editor;
        //----------------------------------
        // ITEMS TO HANDLE
        // Should not replace media/citation/links/keywords/footnotes
        // Should not replace if selection is not collapsed
        // Should not replace if selection is inside a link
        // Should not replace if selection is inside a keyword
        // Should toggle right hand panel and populate releasenotes comment thread
        // Save and reload
        //Restrict further commenting
        //Change UI of comment thread
        //Disable Approve/Reject/Resolve
        //Remove icon if user delete/cancels the thread.
        // Should create new property in JSON to differentiate editorial and release notes.
        // Should not allow new comments to add on thread.
        // Focus needs to be inside comment thread upon release note creation
        //

        //----------------------------------
        const threadId = rawThreadId ?? randomUUIDGenerator().replaceAll('-', '');
        const threadId2 = rawThreadId2 ?? randomUUIDGenerator().replaceAll('-', '').slice(0, 5);
        editor.model.enqueueChange({ isUndoable: false }, (writer) => {

            const selection = editor.model.document.selection;
            const rangeCount = [...editor.model.document.selection.getRanges()];

            if (rangeCount.length === 1) {
                const posToCreateReleaseNotes = selection.getLastPosition();
              
                const dataToReplaceInReleaseNotesRDFA = {
                    '{threadIDOfCommentThread}': threadId,
                    '{baseHrefofDoc}': this.editor.baseHrefForLinkPlugin
                };

                const attrToUpdate = { ...releaseNotesRDFAAttributes }

                for (const attributes in attrToUpdate) {
                    const updatedAttrValue = attrToUpdate[attributes].replace(/{\w+}/g, (placeholderToReplace) => {
                        if (placeholderToReplace in dataToReplaceInReleaseNotesRDFA) {
                            return dataToReplaceInReleaseNotesRDFA[placeholderToReplace];
                        }
                    });
                    attrToUpdate[attributes] = updatedAttrValue;
                }


                const releaseNotes = writer.createElement(RELEASE_NOTES, { ...attrToUpdate });
                writer.insert(releaseNotes, posToCreateReleaseNotes);
                const range = writer.createRangeOn(releaseNotes);
                writer.setSelection(range);
                // Retaining thread3 for markerbased approach later to create comment thread in RH

                writer.addMarker(`comment:${threadId}:${threadId2}`, { range, usingOperation: false, affectsData: true });
                editor.fire('insertreleasenotes', {
                    threadId,
                    isUMCReleaseNote
                });
            }
        });
    }

    refresh() {
        // Delegate to full detection so CKEditor's internal refresh() calls
        // (triggered on every model/selection change) never blindly reset isEnabled.
        this.enableDisableReleaseNotes();
        [KEYUP, CLICK, SELECTION_CHANGE].forEach(event => {
            this.listenTo(this.editor.editing.view.document, event, () => {
                this.enableDisableReleaseNotes();
            });
        })

        /**
         * Check if Ctrl + A is pressed or 'Select All' plugin is executed
         */
        const selectAll = this.editor.commands.get('selectAll');
        selectAll?.on('execute', () => {
            this.enableDisableReleaseNotes();
        });

    }

    /**
     * Returns true if the given model node is inside a link element,
     * walking up ancestors to handle track-change-wrapped links.
     * @private
     * @param {*} node
     * @returns {boolean}
     */
    _isInsideLink(node) {
        return !!node && (
            node.hasAttribute?.(LINK_HREF) ||
            node.getAncestors?.().some(el =>
                el.getAttribute?.(CLASS) === REF ||
                el.getAttribute?.(REL) === linksTo
            )
        );
    }

    /**
     * Returns true if the selection or cursor is anywhere inside a link context.
     * Handles non-collapsed selections (via walker), collapsed cursor positions,
     * and TC-wrapped fresh links where adjacent nodes are wrapper elements.
     * @private
     * @param {*} selection
     * @returns {boolean}
     */
    _isInLinkContext(selection) {
        /*
         * For non-collapsed selections, walk every item in the selected range.
         * If any text node is inside a link, the selection overlaps a link.
         */
        const isInsideLinkByWalker = [...selection.getRanges()].some(range =>
            [...range.getWalker()].some(item => this._isInsideLink(item?.item?.textNode))
        );
        if (isInsideLinkByWalker) return true;

        /*
         * For collapsed cursor positions the walker yields no items, so check
         * the nodes immediately around the cursor position instead.
         */
        const focus = selection.focus;
        const candidates = [focus?.textNode, focus?.nodeBefore, focus?.nodeAfter];

        /*
         * Track-change-wrapped links place the linked text inside a TC wrapper element
         * (e.g. <insertion:...>) rather than directly at the cursor. In that case,
         * nodeBefore/nodeAfter is the wrapper element itself, not the text node.
         * So also check the last child of nodeBefore and first child of nodeAfter
         * to catch link text sitting inside a TC wrapper at the boundary.
         */
        if (focus?.nodeBefore?.childCount > 0) {
            candidates.push(focus.nodeBefore.getChild(focus.nodeBefore.childCount - 1));
        }
        if (focus?.nodeAfter?.childCount > 0) {
            candidates.push(focus.nodeAfter.getChild(0));
        }
        return candidates.some(node => this._isInsideLink(node));
    }

    /**
     * Disables Releasenote icon in toolbar if cursor is inside link/keyword/comments
     * @private
     * @returns {void}
     */
    enableDisableReleaseNotes() {
        const { selection } = this.editor.model.document;
        const isFocused = this.editor.editing.view.document.isFocused;
        const isLinkClicked = subtemplateUtils(this.editor, null);
        const hasSelectedComments = checkSelectedComments(this.editor);

        this.isEnabled = isFocused && !isLinkClicked && !this._isInLinkContext(selection) && !hasSelectedComments;
    }
}